import { isDoorType, isSlidingDoorType, isDoubleDoorType, isWindowType, isVoidType, isAreaSymbolType, WallType, SingleLineType, RulerLineType, SymbolType, DoorType, DoubleDoorType, WindowType, VoidType, WallConnectionMiddle, WallConnectionStart, WallConnectionEnd, isRulerLineType, isSingleLineType, AreaSymbolType } from "../types/floorplannerTypes";
import { FloorplannerStore } from "./floorplannerStore";
import { renderStore } from "./renderStore";
import * as THREE from "three";
import { runInAction } from "mobx";
import { calculateLineVertices } from "../components/FloorPlan/calculateLineVertices";
import { MAX_INTERACTION_PLANE_HALF_SIZE } from "../constants/canvas";
import { calculateArcLength, getWallArcLength } from "../utils/arcUtils";
import { getSymbolInsertGeometry, getSymbolRenderCenterOffset } from "../utils/symbolAttachmentGeometry";
import { isFloorplannerDebugLoggingEnabled } from "../utils/debugLogging";

const MAX_OBJECT_SIZE = 300; // Maximum size for any object in meters
const DESIGN_BOUNDS = 10000; // Design area bounds (±10000m from origin)
const MAX_DISTANCE_FROM_ORIGIN = 5000; // Maximum distance from origin for object center (±5000m)
const MAX_REASONABLE_DIMENSION = 1000; // Maximum reasonable dimension for a floorplan (width or height)
const MIN_WALL_LENGTH = 0.1; // Minimum wall length to prevent zero-area errors (10cm - must be longer than typical wall width)
const MAX_WALL_LENGTH = 100; // Maximum wall length (from floorplannerStore)
const MAX_WALL_WIDTH = 1.0; // Maximum wall width (1m)
const MIN_WALL_WIDTH = 0.01; // Minimum wall width (1cm)
const MIN_LENGTH_TO_WIDTH_RATIO = 3.0; // Wall length must be at least 3x its width to avoid zero-area geometry
const INTEGRITY_RESET_ZOOM_MAX_ATTEMPTS = 3;

type IntegrityResetZoomResult = {
    hasValidBox?: boolean;
};

type IntegrityEditorStore = {
    resetZoom?: (options?: { warnOnInvalidBounds?: boolean }) => IntegrityResetZoomResult | undefined;
};

const yieldEventLoop = (): Promise<void> =>
    new Promise((resolve) => {
        setTimeout(resolve, 0);
    });

/**
 * Rebuild wall meshes after integrity mutations, then fit the camera once the
 * scene has a usable bounding box. Stops immediately if bounds stay invalid so
 * we do not stall the main thread with a long retry loop.
 */
async function resetCameraAfterIntegrityRedraw(
    store: FloorplannerStore,
    shouldLogDetails: boolean,
    resetCamera: boolean,
): Promise<void> {
    renderStore.clearWallShapes("");
    await store.triggerFullWallRedraw();

    // Collaborative reloads restore the viewer's existing camera themselves.
    // Do not race that restore with a delayed integrity-driven reset.
    if (!resetCamera) {
        return;
    }

    const globalEditorStore = (
        globalThis as typeof globalThis & { __boniEditorStore?: IntegrityEditorStore }
    ).__boniEditorStore;

    if (!globalEditorStore?.resetZoom) {
        console.warn("[integrity] EditorStore not available for resetZoom after integrity fixes");
        return;
    }

    for (let attempt = 1; attempt <= INTEGRITY_RESET_ZOOM_MAX_ATTEMPTS; attempt += 1) {
        const resetResult = globalEditorStore.resetZoom({
            warnOnInvalidBounds: attempt === INTEGRITY_RESET_ZOOM_MAX_ATTEMPTS,
        });
        if (resetResult?.hasValidBox) {
            if (shouldLogDetails) {
                console.log(
                    `[integrity] resetZoom succeeded after integrity redraw (attempt ${attempt})`
                );
            }
            return;
        }
        if (attempt < INTEGRITY_RESET_ZOOM_MAX_ATTEMPTS) {
            await yieldEventLoop();
        }
    }

    console.warn("[integrity] resetZoom skipped after integrity fixes; scene bounds still invalid");
}

/**
 * Constrains a wall or line to maximum length of 300m.
 * Returns whether the wall was modified.
 */
function constrainWallOrLineSize(
    object: WallType | SingleLineType | RulerLineType,
    store: FloorplannerStore
): boolean {
    // Calculate actual length - for arc walls, use arc length; for straight walls, use direct distance
    let length: number;
    if ('wallWidth' in object) {
        const wall = object as WallType;
        if (wall.controlPoint && 
            Number.isFinite(wall.controlPoint.x) && Number.isFinite(wall.controlPoint.y)) {
            // Arc wall - calculate actual arc length
            length = calculateArcLength(wall.start, wall.controlPoint, wall.end);
        } else {
            // Straight wall - use direct distance
            length = wall.start.distanceTo(wall.end);
        }
    } else {
        // Single line or ruler line - always use direct distance
        length = object.start.distanceTo(object.end);
    }
    
    // For walls, use MAX_WALL_LENGTH (100m), for lines use MAX_OBJECT_SIZE (300m)
    const maxLength = ('wallWidth' in object) ? MAX_WALL_LENGTH : MAX_OBJECT_SIZE;
    
    // For walls, ensure length is at least as long as the wall width to prevent zero-area geometry
    let minRequiredLength = MIN_WALL_LENGTH;
    if ('wallWidth' in object) {
        const wall = object as WallType;
        const wallWidth = wall.wallWidth || store.wallWidth || 0.1;
        // Wall must be at least as long as its width, but not less than MIN_WALL_LENGTH
        minRequiredLength = Math.max(MIN_WALL_LENGTH, wallWidth * 1.1); // Add 10% margin for safety
    }
    
    if (length <= maxLength && length >= minRequiredLength) {
        return false;
    }

    // If length is too small, set to minimum required length
    if (length < minRequiredLength) {
        console.warn(
            `[integrity] Wall/line ${object.id} is too short (${length.toFixed(4)}m), setting to minimum length ${minRequiredLength.toFixed(4)}m`
        );
        const direction = object.end.clone().sub(object.start);
        if (direction.length() < 1e-6) {
            // If start and end are the same, create a small horizontal line
            direction.set(1, 0);
        }
        direction.normalize();
        const newEnd = object.start.clone().add(direction.multiplyScalar(minRequiredLength));
        
        runInAction(() => {
            if ('wallWidth' in object) {
                const wall = object as WallType;
                store.wallsMap.set(wall.id, { ...wall, end: newEnd });
            } else if (isSingleLineType(object)) {
                store.singleLinesMap.set(object.id, { ...object, end: newEnd });
            } else if (isRulerLineType(object)) {
                store.rulerLinesMap.set(object.id, { ...object, end: newEnd });
            }
        });
        return true;
    }

    // Constrain to max length
    // For arc walls, convert to straight wall if arc length exceeds limit
    // For straight walls/lines, just move the end point
    const isArcWall = ('wallWidth' in object) && (object as WallType).controlPoint;
    const straightLineDistance = object.start.distanceTo(object.end);
    
    console.warn(
        `[integrity] Constraining ${object.type} ${object.id} from ${length.toFixed(2)}m to ${maxLength}m ` +
        `(straight-line: ${straightLineDistance.toFixed(2)}m, isArc: ${!!isArcWall}, ` +
        `start: (${object.start.x.toFixed(2)}, ${object.start.y.toFixed(2)}), ` +
        `end: (${object.end.x.toFixed(2)}, ${object.end.y.toFixed(2)}))`
    );

    // Update the object
    runInAction(() => {
        if ('wallWidth' in object) {
            // It's a WallType
            const wall = object as WallType;
            
            // If it's an arc wall and exceeds limit, convert to straight wall
            if (wall.controlPoint && length > maxLength) {
                // Convert arc to straight line by removing controlPoint and clamping end point
                const direction = wall.end.clone().sub(wall.start).normalize();
                const newEnd = wall.start.clone().add(direction.multiplyScalar(maxLength));
                store.wallsMap.set(wall.id, {
                    ...wall,
                    end: newEnd,
                    controlPoint: undefined, // Remove controlPoint to make it straight
                });
                console.warn(
                    `[integrity] Converted arc wall ${wall.id} to straight wall (removed controlPoint, clamped to ${maxLength}m)`
                );
            } else {
                // Straight wall - just move end point
                const direction = wall.end.clone().sub(wall.start).normalize();
                const newEnd = wall.start.clone().add(direction.multiplyScalar(maxLength));
                store.wallsMap.set(wall.id, {
                    ...wall,
                    end: newEnd,
                });
            }
        } else if (isSingleLineType(object)) {
            // It's a SingleLineType
            const direction = object.end.clone().sub(object.start).normalize();
            const newEnd = object.start.clone().add(direction.multiplyScalar(maxLength));
            store.singleLinesMap.set(object.id, {
                ...object,
                end: newEnd,
            });
        } else if (isRulerLineType(object)) {
            // It's a RulerLineType
            const direction = object.end.clone().sub(object.start).normalize();
            const newEnd = object.start.clone().add(direction.multiplyScalar(maxLength));
            store.rulerLinesMap.set(object.id, {
                ...object,
                end: newEnd,
            });
        }
    });

    return true;
}

/**
 * Checks and fixes connections (edgeConnections, centerConnections, attachments) 
 * that are positioned beyond 300m from wall endpoints.
 */
function checkAndFixConnections(store: FloorplannerStore): number {
    let fixedCount = 0;

    store.wallsMap.forEach((wall) => {
        const wallLength = getWallArcLength(wall);
        const maxConnectionDistance = Math.min(wallLength, MAX_OBJECT_SIZE);

        // Check edgeConnections
        if (wall.edgeConnections) {
            wall.edgeConnections.forEach((connection) => {
                let needsFix = false;
                const updates: Partial<typeof connection> = {};

                // First, validate edge connection coordinates for NaN/Infinity
                // This is critical - invalid coordinates cause zero-area wall shapes
                if (!Number.isFinite(connection.sourceEdgePoint.x) ||
                    !Number.isFinite(connection.sourceEdgePoint.y)) {
                    console.warn(
                        `[integrity] Fixing invalid sourceEdgePoint coordinates for edgeConnection ${connection.id} on wall ${wall.id}`
                    );
                    // Recalculate sourceEdgePoint from wall geometry
                    const wallDir = wall.end.clone().sub(wall.start).normalize();
                    const wallPerp = new THREE.Vector2(-wallDir.y, wallDir.x);
                    const halfWidth = (wall.wallWidth || store.wallWidth || 0.1) / 2;
                    const basePoint = connection.sourceEndpoint === 'start' ? wall.start : wall.end;
                    updates.sourceEdgePoint = basePoint.clone().add(wallPerp.multiplyScalar(halfWidth));
                    needsFix = true;
                }

                // Check targetEdgePoint for invalid coordinates
                const targetWall = store.wallsMap.get(connection.targetWallId);
                if (targetWall) {
                    if (!Number.isFinite(connection.targetEdgePoint.x) ||
                        !Number.isFinite(connection.targetEdgePoint.y)) {
                        console.warn(
                            `[integrity] Fixing invalid targetEdgePoint coordinates for edgeConnection ${connection.id} on wall ${wall.id}`
                        );
                        // Recalculate targetEdgePoint from target wall geometry
                        const targetDir = targetWall.end.clone().sub(targetWall.start).normalize();
                        const targetPerp = new THREE.Vector2(-targetDir.y, targetDir.x);
                        const targetHalfWidth = (targetWall.wallWidth || store.wallWidth || 0.1) / 2;
                        const targetBasePoint = connection.targetEndpoint === 'start' ? targetWall.start :
                                                connection.targetEndpoint === 'end' ? targetWall.end :
                                                targetWall.start.clone().lerp(targetWall.end, 0.5);
                        updates.targetEdgePoint = targetBasePoint.clone().add(targetPerp.multiplyScalar(targetHalfWidth));
                        needsFix = true;
                    }
                }

                // After validating coordinates, check distance bounds
                const sourceEdgePoint = updates.sourceEdgePoint || connection.sourceEdgePoint;
                const sourceDistFromStart = sourceEdgePoint.distanceTo(wall.start);
                const sourceDistFromEnd = sourceEdgePoint.distanceTo(wall.end);
                const minSourceDist = Math.min(sourceDistFromStart, sourceDistFromEnd);

                if (minSourceDist > maxConnectionDistance) {
                    // Move to closest endpoint
                    const closestEndpoint = sourceDistFromStart < sourceDistFromEnd ? wall.start : wall.end;
                    updates.sourceEdgePoint = closestEndpoint.clone();
                    needsFix = true;
                }

                // Check targetEdgePoint distance bounds
                if (targetWall) {
                    const targetEdgePoint = updates.targetEdgePoint || connection.targetEdgePoint;
                    const targetDistFromStart = targetEdgePoint.distanceTo(targetWall.start);
                    const targetDistFromEnd = targetEdgePoint.distanceTo(targetWall.end);
                    const minTargetDist = Math.min(targetDistFromStart, targetDistFromEnd);
                    const targetWallLength = targetWall.start.distanceTo(targetWall.end);
                    const maxTargetConnectionDistance = Math.min(targetWallLength, MAX_OBJECT_SIZE);

                    if (minTargetDist > maxTargetConnectionDistance) {
                        // Move to closest endpoint
                        const closestEndpoint = targetDistFromStart < targetDistFromEnd ? targetWall.start : targetWall.end;
                        updates.targetEdgePoint = closestEndpoint.clone();
                        needsFix = true;
                    }
                }

                if (needsFix) {
                    console.warn(
                        `[integrity] Fixing edgeConnection ${connection.id} on wall ${wall.id} - moving connection points within bounds`
                    );
                    runInAction(() => {
                        Object.assign(connection, updates);
                    });
                    fixedCount++;
                }
            });
        }

        // Check centerConnections
        if (wall.centerConnections) {
            wall.centerConnections.forEach((connection) => {
                // sourcePosition and targetPosition are distances along the wall (0 = start, wallLength = end)
                // WallConnectionMiddle (5000) represents middle point
                let needsFix = false;
                let newSourcePosition = connection.sourcePosition;
                let newTargetPosition = connection.targetPosition;

                // Clamp sourcePosition to actual wall length (not just MAX_OBJECT_SIZE)
                // This ensures the position is always within the wall bounds
                // IMPORTANT: Don't modify special constants (WallConnectionStart=0, WallConnectionMiddle=5000, WallConnectionEnd=10000)
                if (connection.sourcePosition !== WallConnectionStart && 
                    connection.sourcePosition !== WallConnectionMiddle && 
                    connection.sourcePosition !== WallConnectionEnd) {
                    if (connection.sourcePosition > wallLength) {
                        // Position exceeds wall length - clamp to wall end
                        newSourcePosition = wallLength;
                        needsFix = true;
                    } else if (connection.sourcePosition > maxConnectionDistance) {
                        // Position exceeds max connection distance - clamp to max
                        newSourcePosition = Math.min(connection.sourcePosition, maxConnectionDistance);
                        needsFix = true;
                    } else if (connection.sourcePosition < 0) {
                        // Position is negative - clamp to start
                        newSourcePosition = 0;
                        needsFix = true;
                    }
                }

                const targetWall = store.wallsMap.get(connection.id);
                if (targetWall) {
                    const targetWallLength = targetWall.start.distanceTo(targetWall.end);
                    const maxTargetConnectionDistance = Math.min(targetWallLength, MAX_OBJECT_SIZE);
                    
                    // IMPORTANT: Don't modify special constants (WallConnectionStart=0, WallConnectionMiddle=5000, WallConnectionEnd=10000)
                    if (connection.targetPosition !== WallConnectionStart && 
                        connection.targetPosition !== WallConnectionMiddle && 
                        connection.targetPosition !== WallConnectionEnd) {
                        if (connection.targetPosition > targetWallLength) {
                            // Position exceeds target wall length - clamp to wall end
                            newTargetPosition = targetWallLength;
                            needsFix = true;
                        } else if (connection.targetPosition > maxTargetConnectionDistance) {
                            // Position exceeds max connection distance - clamp to max
                            newTargetPosition = Math.min(connection.targetPosition, maxTargetConnectionDistance);
                            needsFix = true;
                        } else if (connection.targetPosition < 0) {
                            // Position is negative - clamp to start
                            newTargetPosition = 0;
                            needsFix = true;
                        }
                    }
                }

                if (needsFix) {
                    console.warn(
                        `[integrity] Fixing centerConnection on wall ${wall.id} to ${connection.id} - constraining positions within bounds (source: ${connection.sourcePosition.toFixed(2)} -> ${newSourcePosition.toFixed(2)}, target: ${connection.targetPosition.toFixed(2)} -> ${newTargetPosition.toFixed(2)})`
                    );
                    runInAction(() => {
                        connection.sourcePosition = newSourcePosition;
                        connection.targetPosition = newTargetPosition;
                    });
                    fixedCount++;
                }
            });
        }

        // Check symbol attachments
        if (wall.symbolAttachments) {
            wall.symbolAttachments.forEach((attachment) => {
                if (attachment.positionFromStart > maxConnectionDistance) {
                    console.warn(
                        `[integrity] Fixing symbol attachment ${attachment.symbolId} on wall ${wall.id} - moving from ${attachment.positionFromStart.toFixed(2)}m to ${maxConnectionDistance.toFixed(2)}m`
                    );
                    runInAction(() => {
                        attachment.positionFromStart = maxConnectionDistance;
                        
                        // Update symbol position if it exists
                        const symbol = store.symbolsMap.get(attachment.symbolId);
                        if (symbol && symbol.attachedTo === wall.id) {
                            const insertGeom = getSymbolInsertGeometry(wall, symbol, maxConnectionDistance, store.doorFrameWidth);
                            const renderCenterOffset = getSymbolRenderCenterOffset(wall, symbol, maxConnectionDistance, store.doorFrameWidth);
                            const newPosition = insertGeom.midpoint
                                .clone()
                                .sub(insertGeom.tangent.clone().multiplyScalar(renderCenterOffset));
                            store.symbolsMap.set(attachment.symbolId, {
                                ...symbol,
                                position: newPosition,
                                rotation: Math.atan2(insertGeom.tangent.y, insertGeom.tangent.x) || 0.0001,
                            });
                        }
                    });
                    fixedCount++;
                }
            });
        }
    });

    return fixedCount;
}

/**
 * Detects and fixes outlier objects that are far from the main cluster.
 * Returns the number of objects that were moved.
 */
function detectAndFixOutliers(store: FloorplannerStore): number {
    let fixedCount = 0;
    
    // Collect all object center points
    const objectCenters: Array<{ id: string; type: 'wall' | 'singleLine' | 'rulerLine' | 'symbol'; center: THREE.Vector2 }> = [];
    
    store.wallsMap.forEach((wall) => {
        const center = wall.start.clone().add(wall.end).multiplyScalar(0.5);
        objectCenters.push({ id: wall.id, type: 'wall', center });
    });
    
    store.singleLinesMap.forEach((line) => {
        const center = line.start.clone().add(line.end).multiplyScalar(0.5);
        objectCenters.push({ id: line.id, type: 'singleLine', center });
    });
    
    store.rulerLinesMap.forEach((line) => {
        const center = line.start.clone().add(line.end).multiplyScalar(0.5);
        objectCenters.push({ id: line.id, type: 'rulerLine', center });
    });
    
    store.symbolsMap.forEach((symbol) => {
        objectCenters.push({ id: symbol.id, type: 'symbol', center: symbol.position.clone() });
    });
    
    if (objectCenters.length < 2) return 0;
    
    // Calculate median center (more robust than mean for outlier detection)
    const sortedX = objectCenters.map(o => o.center.x).sort((a, b) => a - b);
    const sortedY = objectCenters.map(o => o.center.y).sort((a, b) => a - b);
    const medianX = sortedX[Math.floor(sortedX.length / 2)];
    const medianY = sortedY[Math.floor(sortedY.length / 2)];
    const medianCenter = new THREE.Vector2(medianX, medianY);
    
    // Calculate median absolute deviation (MAD) for outlier detection
    const distances = objectCenters.map(o => o.center.distanceTo(medianCenter));
    const sortedDistances = distances.sort((a, b) => a - b);
    const medianDistance = sortedDistances[Math.floor(sortedDistances.length / 2)];
    const mad = medianDistance || 1; // Fallback to 1 if all points are the same
    
    // Objects more than 3 * MAD away from median are considered outliers
    // But also check if they're more than MAX_REASONABLE_DIMENSION/2 away (500m)
    const outlierThreshold = Math.max(3 * mad, MAX_REASONABLE_DIMENSION / 2);
    
    const outliers: Array<{ id: string; type: string; distance: number; center: THREE.Vector2 }> = [];
    objectCenters.forEach(obj => {
        const distance = obj.center.distanceTo(medianCenter);
        if (distance > outlierThreshold) {
            outliers.push({ id: obj.id, type: obj.type, distance, center: obj.center });
        }
    });
    
    if (outliers.length === 0) return 0;
    
    console.warn(`[integrity] Detected ${outliers.length} outlier object(s) far from main cluster:`, {
        medianCenter: { x: medianX.toFixed(2), y: medianY.toFixed(2) },
        outlierThreshold: outlierThreshold.toFixed(2),
        outliers: outliers.map(o => ({
            id: o.id,
            type: o.type,
            distance: o.distance.toFixed(2),
            position: { x: o.center.x.toFixed(2), y: o.center.y.toFixed(2) }
        }))
    });
    
    // Move outliers to the median center (main cluster)
    runInAction(() => {
        outliers.forEach(outlier => {
            if (outlier.type === 'wall') {
                const wall = store.wallsMap.get(outlier.id);
                if (wall) {
                    const offset = medianCenter.clone().sub(outlier.center);
                    const oldStart = wall.start.clone();
                    wall.start.add(offset);
                    wall.end.add(offset);
                    if (wall.controlPoint) {
                        wall.controlPoint.add(offset);
                    }
                    fixedCount++;
                    console.warn(`[integrity] Moved outlier wall ${outlier.id} from (${oldStart.x.toFixed(2)}, ${oldStart.y.toFixed(2)}) to (${wall.start.x.toFixed(2)}, ${wall.start.y.toFixed(2)})`);
                }
            } else if (outlier.type === 'singleLine') {
                const line = store.singleLinesMap.get(outlier.id);
                if (line) {
                    const offset = medianCenter.clone().sub(outlier.center);
                    line.start.add(offset);
                    line.end.add(offset);
                    if (line.controlPoint) {
                        line.controlPoint.add(offset);
                    }
                    fixedCount++;
                }
            } else if (outlier.type === 'rulerLine') {
                const line = store.rulerLinesMap.get(outlier.id);
                if (line) {
                    const offset = medianCenter.clone().sub(outlier.center);
                    line.start.add(offset);
                    line.end.add(offset);
                    if (line.controlPoint) {
                        line.controlPoint.add(offset);
                    }
                    fixedCount++;
                }
            } else if (outlier.type === 'symbol') {
                const symbol = store.symbolsMap.get(outlier.id);
                if (symbol) {
                    const offset = medianCenter.clone().sub(outlier.center);
                    symbol.position.add(offset);
                    if (isAreaSymbolType(symbol) && symbol.vertices) {
                        symbol.vertices.forEach(v => v.add(offset));
                    }
                    fixedCount++;
                }
            }
        });
    });
    
    return fixedCount;
}

/**
 * Checks if objects are outside design bounds and translates them to center.
 * Returns the number of objects that were translated.
 */
function checkAndFixObjectPositions(store: FloorplannerStore): number {
    let fixedCount = 0;

    // First, detect and fix outliers (objects far from main cluster)
    const outliersFixed = detectAndFixOutliers(store);
    fixedCount += outliersFixed;
    
    if (outliersFixed > 0) {
        console.log(`[integrity] Fixed ${outliersFixed} outlier object(s), recalculating bounds...`);
    }

    // Calculate center of all objects (after outlier fixes)
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let hasObjects = false;
    let pointCount = 0;

    const registerPoint = (point: THREE.Vector2) => {
        if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
        hasObjects = true;
        pointCount++;
    };

    // Collect all object points - use Array.from to get a snapshot to avoid iteration issues
    const walls = Array.from(store.wallsMap.values());
    const singleLines = Array.from(store.singleLinesMap.values());
    const rulerLines = Array.from(store.rulerLinesMap.values());
    const symbols = Array.from(store.symbolsMap.values());
    
    // Reduced logging - only log if translation is needed
    
    walls.forEach((wall) => {
        registerPoint(wall.start);
        registerPoint(wall.end);
        if (wall.controlPoint) registerPoint(wall.controlPoint);
    });

    singleLines.forEach((line) => {
        registerPoint(line.start);
        registerPoint(line.end);
        if (line.controlPoint) registerPoint(line.controlPoint);
    });

    rulerLines.forEach((line) => {
        registerPoint(line.start);
        registerPoint(line.end);
        if (line.controlPoint) registerPoint(line.controlPoint);
    });

    symbols.forEach((symbol) => {
        registerPoint(symbol.position);
        if (isAreaSymbolType(symbol) && symbol.vertices) {
            symbol.vertices.forEach(registerPoint);
        }
    });

    if (!hasObjects) {
        console.error(`[integrity] checkAndFixObjectPositions: No valid objects found after collecting points! This suggests all objects were removed or have invalid coordinates.`);
        console.error(`[integrity] Store state: walls=${store.wallsMap.size}, singleLines=${store.singleLinesMap.size}, rulerLines=${store.rulerLinesMap.size}, symbols=${store.symbolsMap.size}`);
        return 0;
    }
    
    // Calculate center of all objects
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const width = maxX - minX;
    const height = maxY - minY;
    
    const shouldLogDetails = isFloorplannerDebugLoggingEnabled();

    // Log detailed bounds information for debugging
    if (shouldLogDetails) {
        console.log(`[integrity] checkAndFixObjectPositions: Bounds calculation:`, {
            pointCount,
            minX: minX.toFixed(2),
            minY: minY.toFixed(2),
            maxX: maxX.toFixed(2),
            maxY: maxY.toFixed(2),
            centerX: centerX.toFixed(2),
            centerY: centerY.toFixed(2),
            width: width.toFixed(2),
            height: height.toFixed(2),
            storeCounts: {
                walls: store.wallsMap.size,
                singleLines: store.singleLinesMap.size,
                rulerLines: store.rulerLinesMap.size,
                symbols: store.symbolsMap.size
            }
        });
    }
    
    // Check if center is too far from origin OR if any object is outside design bounds
    // Also check if the bounds extend too far from origin (even if center is OK)
    const centerDistanceFromOrigin = Math.sqrt(centerX * centerX + centerY * centerY);
    const centerMaxAxisDistance = Math.max(Math.abs(centerX), Math.abs(centerY));
    const centerOutsideInteractionBounds = centerMaxAxisDistance > MAX_INTERACTION_PLANE_HALF_SIZE;
    
    // Calculate maximum distance from origin for any corner of the bounding box
    const cornerDistances = [
        Math.sqrt(minX * minX + minY * minY),  // bottom-left corner
        Math.sqrt(maxX * maxX + minY * minY),  // bottom-right corner
        Math.sqrt(minX * minX + maxY * maxY),  // top-left corner
        Math.sqrt(maxX * maxX + maxY * maxY)   // top-right corner
    ];
    const maxCornerDistanceFromOrigin = Math.max(...cornerDistances);
    
    // Also check if any single coordinate is too far (for very tall/wide floorplans)
    const maxSingleCoordinateDistance = Math.max(
        Math.abs(minX), Math.abs(minY), Math.abs(maxX), Math.abs(maxY)
    );
    
    // Check if the floorplan dimensions are too large (height or width > 1000m suggests objects are spread too far)
    const hasExcessiveDimensions = width > MAX_REASONABLE_DIMENSION || height > MAX_REASONABLE_DIMENSION;
    
    const needsTranslation = 
        centerOutsideInteractionBounds ||
        centerDistanceFromOrigin > MAX_DISTANCE_FROM_ORIGIN ||
        maxCornerDistanceFromOrigin > MAX_DISTANCE_FROM_ORIGIN ||
        maxSingleCoordinateDistance > MAX_DISTANCE_FROM_ORIGIN ||
        hasExcessiveDimensions ||
        Math.abs(minX) > DESIGN_BOUNDS || 
        Math.abs(minY) > DESIGN_BOUNDS ||
        Math.abs(maxX) > DESIGN_BOUNDS || 
        Math.abs(maxY) > DESIGN_BOUNDS;

    if (shouldLogDetails) {
        console.log(`[integrity] checkAndFixObjectPositions:`, {
            bounds: {
                min: { x: minX.toFixed(2), y: minY.toFixed(2) },
                max: { x: maxX.toFixed(2), y: maxY.toFixed(2) },
                center: { x: centerX.toFixed(2), y: centerY.toFixed(2) },
                width: width.toFixed(2),
                height: height.toFixed(2)
            },
            checks: {
                centerDistanceFromOrigin: centerDistanceFromOrigin.toFixed(2),
                centerMaxAxisDistance: centerMaxAxisDistance.toFixed(2),
                maxCornerDistanceFromOrigin: maxCornerDistanceFromOrigin.toFixed(2),
                maxSingleCoordinateDistance: maxSingleCoordinateDistance.toFixed(2),
                maxDistanceFromOriginLimit: MAX_DISTANCE_FROM_ORIGIN,
                interactionBoundsLimit: MAX_INTERACTION_PLANE_HALF_SIZE,
                maxReasonableDimension: MAX_REASONABLE_DIMENSION,
                centerExceedsMaxDistance: centerDistanceFromOrigin > MAX_DISTANCE_FROM_ORIGIN,
                centerOutsideInteractionBounds,
                cornerExceedsMaxDistance: maxCornerDistanceFromOrigin > MAX_DISTANCE_FROM_ORIGIN,
                singleCoordinateExceedsMaxDistance: maxSingleCoordinateDistance > MAX_DISTANCE_FROM_ORIGIN,
                hasExcessiveDimensions: hasExcessiveDimensions,
                widthExceedsLimit: width > MAX_REASONABLE_DIMENSION,
                heightExceedsLimit: height > MAX_REASONABLE_DIMENSION,
                minXOutsideBounds: Math.abs(minX) > DESIGN_BOUNDS,
                minYOutsideBounds: Math.abs(minY) > DESIGN_BOUNDS,
                maxXOutsideBounds: Math.abs(maxX) > DESIGN_BOUNDS,
                maxYOutsideBounds: Math.abs(maxY) > DESIGN_BOUNDS,
                needsTranslation
            }
        });
    }

    if (!needsTranslation) {
        if (shouldLogDetails) {
            console.log(`[integrity] checkAndFixObjectPositions: No translation needed`);
        }
        return 0;
    }

    // Calculate translation to move center to origin
    const translationX = -centerX;
    const translationY = -centerY;
    
    // Even if center is at origin, if dimensions are excessive, we should scale down
    // But for now, we'll still translate if needed (center might be slightly off)
    // The key issue is that very tall/narrow floorplans need special handling
    if (Math.abs(translationX) < 0.01 && Math.abs(translationY) < 0.01 && !hasExcessiveDimensions) {
        console.log(`[integrity] checkAndFixObjectPositions: Center is already at origin (${centerX.toFixed(2)}, ${centerY.toFixed(2)}) and dimensions are reasonable, skipping translation`);
        return 0;
    }
    
    // If dimensions are excessive but center is at origin, log a warning
    // The camera zoom calculation should handle this, but we should still ensure objects are in reasonable range
    if (hasExcessiveDimensions && Math.abs(translationX) < 0.01 && Math.abs(translationY) < 0.01) {
        console.warn(`[integrity] checkAndFixObjectPositions: Floorplan has excessive dimensions (${width.toFixed(2)}m x ${height.toFixed(2)}m) but center is at origin. Camera zoom will need to be adjusted.`);
        // Don't skip - we might still want to do some adjustments
    }
    
    // Safety check: only skip translation if bounds are truly invalid (all points at same location, or invalid dimensions)
    // A center at (0, 0) is valid if the floorplan is symmetric around the origin
    const hasValidDimensions = width > 0 && height > 0 && Number.isFinite(width) && Number.isFinite(height);
    if (!hasValidDimensions && pointCount > 0) {
        console.error(
            `[integrity] ERROR: Invalid bounds dimensions! This suggests a bounds calculation error.`,
            `Actual bounds: min(${minX.toFixed(2)}, ${minY.toFixed(2)}), max(${maxX.toFixed(2)}, ${maxY.toFixed(2)}), width: ${width.toFixed(2)}, height: ${height.toFixed(2)}`,
            `Skipping translation to prevent data loss.`
        );
        return 0;
    }

    console.warn(
        `[integrity] Translating floorplan objects to center - center was at (${centerX.toFixed(2)}, ${centerY.toFixed(2)}), translating by (${translationX.toFixed(2)}, ${translationY.toFixed(2)})`
    );

    // Translate all walls
    console.log(`[integrity] Starting translation of ${store.wallsMap.size} walls, ${store.singleLinesMap.size} single lines, ${store.rulerLinesMap.size} ruler lines, ${store.symbolsMap.size} symbols`);
    
    runInAction(() => {
        const wallIds = Array.from(store.wallsMap.keys());
        console.log(`[integrity] Translating ${wallIds.length} walls`);
        
        wallIds.forEach((wallId) => {
            const wall = store.wallsMap.get(wallId);
            if (!wall) {
                console.warn(`[integrity] Wall ${wallId} not found during translation`);
                return;
            }
            
            const newWall = {
                ...wall,
                start: new THREE.Vector2(wall.start.x + translationX, wall.start.y + translationY),
                end: new THREE.Vector2(wall.end.x + translationX, wall.end.y + translationY),
            };
            if (wall.controlPoint) {
                newWall.controlPoint = new THREE.Vector2(
                    wall.controlPoint.x + translationX,
                    wall.controlPoint.y + translationY
                );
            }
            // Translate edge connection points
            if (wall.edgeConnections) {
                newWall.edgeConnections = wall.edgeConnections.map(conn => ({
                    ...conn,
                    sourceEdgePoint: new THREE.Vector2(
                        conn.sourceEdgePoint.x + translationX,
                        conn.sourceEdgePoint.y + translationY
                    ),
                    targetEdgePoint: new THREE.Vector2(
                        conn.targetEdgePoint.x + translationX,
                        conn.targetEdgePoint.y + translationY
                    ),
                }));
            }
            store.wallsMap.set(wall.id, newWall);
            fixedCount++;
        });

        // Translate all single lines
        const singleLineIds = Array.from(store.singleLinesMap.keys());
        console.log(`[integrity] Translating ${singleLineIds.length} single lines`);
        
        singleLineIds.forEach((lineId) => {
            const line = store.singleLinesMap.get(lineId);
            if (!line) {
                console.warn(`[integrity] Single line ${lineId} not found during translation`);
                return;
            }
            
            const newLine = {
                ...line,
                start: new THREE.Vector2(line.start.x + translationX, line.start.y + translationY),
                end: new THREE.Vector2(line.end.x + translationX, line.end.y + translationY),
            };
            if (line.controlPoint) {
                newLine.controlPoint = new THREE.Vector2(
                    line.controlPoint.x + translationX,
                    line.controlPoint.y + translationY
                );
            }
            store.singleLinesMap.set(line.id, newLine);
            fixedCount++;
        });

        // Translate all ruler lines
        const rulerLineIds = Array.from(store.rulerLinesMap.keys());
        console.log(`[integrity] Translating ${rulerLineIds.length} ruler lines`);
        
        rulerLineIds.forEach((lineId) => {
            const line = store.rulerLinesMap.get(lineId);
            if (!line) {
                console.warn(`[integrity] Ruler line ${lineId} not found during translation`);
                return;
            }
            
            const newLine = {
                ...line,
                start: new THREE.Vector2(line.start.x + translationX, line.start.y + translationY),
                end: new THREE.Vector2(line.end.x + translationX, line.end.y + translationY),
            };
            if (line.controlPoint) {
                newLine.controlPoint = new THREE.Vector2(
                    line.controlPoint.x + translationX,
                    line.controlPoint.y + translationY
                );
            }
            store.rulerLinesMap.set(line.id, newLine);
            fixedCount++;
        });

        // Translate all symbols
        const symbolIds = Array.from(store.symbolsMap.keys());
        console.log(`[integrity] Translating ${symbolIds.length} symbols`);
        
        symbolIds.forEach((symbolId) => {
            const symbol = store.symbolsMap.get(symbolId);
            if (!symbol) {
                console.warn(`[integrity] Symbol ${symbolId} not found during translation`);
                return;
            }
            
            if (isAreaSymbolType(symbol) && symbol.vertices) {
                const newSymbol: AreaSymbolType = {
                    ...symbol,
                    position: new THREE.Vector2(
                        symbol.position.x + translationX,
                        symbol.position.y + translationY
                    ),
                    vertices: symbol.vertices.map(v => 
                        new THREE.Vector2(v.x + translationX, v.y + translationY)
                    ),
                };
                store.symbolsMap.set(symbol.id, newSymbol);
            } else {
                const newSymbol: SymbolType = {
                    ...symbol,
                    position: new THREE.Vector2(
                        symbol.position.x + translationX,
                        symbol.position.y + translationY
                    ),
                };
                store.symbolsMap.set(symbol.id, newSymbol);
            }
            fixedCount++;
        });
    });
    
    console.log(`[integrity] Translation complete. Translated ${fixedCount} objects. Final counts:`, {
        walls: store.wallsMap.size,
        singleLines: store.singleLinesMap.size,
        rulerLines: store.rulerLinesMap.size,
        symbols: store.symbolsMap.size
    });

    return fixedCount;
}

/**
 * Calculates and logs the bounding box of all objects in the store
 */
function calculateAndLogBounds(store: FloorplannerStore, label: string): { minX: number; minY: number; maxX: number; maxY: number; centerX: number; centerY: number; width: number; height: number } | null {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let pointCount = 0;

    const registerPoint = (point: THREE.Vector2) => {
        if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
        pointCount++;
    };

    // Collect all object points
    store.wallsMap.forEach((wall) => {
        registerPoint(wall.start);
        registerPoint(wall.end);
        if (wall.controlPoint) registerPoint(wall.controlPoint);
    });

    store.singleLinesMap.forEach((line) => {
        registerPoint(line.start);
        registerPoint(line.end);
        if (line.controlPoint) registerPoint(line.controlPoint);
    });

    store.rulerLinesMap.forEach((line) => {
        registerPoint(line.start);
        registerPoint(line.end);
        if (line.controlPoint) registerPoint(line.controlPoint);
    });

    store.symbolsMap.forEach((symbol) => {
        registerPoint(symbol.position);
        if (isAreaSymbolType(symbol) && symbol.vertices) {
            symbol.vertices.forEach(registerPoint);
        }
    });

    if (pointCount === 0 || minX === Number.POSITIVE_INFINITY) {
        console.log(`[integrity] ${label}: No valid objects found`);
        return null;
    }

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const width = maxX - minX;
    const height = maxY - minY;
    const centerDistanceFromOrigin = Math.sqrt(centerX * centerX + centerY * centerY);

    console.log(`[integrity] ${label}:`, {
        bounds: {
            min: { x: minX.toFixed(2), y: minY.toFixed(2) },
            max: { x: maxX.toFixed(2), y: maxY.toFixed(2) },
            center: { x: centerX.toFixed(2), y: centerY.toFixed(2) },
            width: width.toFixed(2),
            height: height.toFixed(2),
            centerDistanceFromOrigin: centerDistanceFromOrigin.toFixed(2)
        },
        objectCounts: {
            walls: store.wallsMap.size,
            singleLines: store.singleLinesMap.size,
            rulerLines: store.rulerLinesMap.size,
            symbols: store.symbolsMap.size,
            totalPoints: pointCount
        }
    });

    return { minX, minY, maxX, maxY, centerX, centerY, width, height };
}

export function checkFloorplannerStoreIntegrity(
    store: FloorplannerStore,
    options: { resetCameraAfterRedraw?: boolean } = {},
) {
    // Clear previous warnings when starting a new integrity check
    // This allows warnings to be logged again if the same issues persist after fixes
    if (typeof window !== 'undefined' && (window as any).__clearFillMeshWarnings) {
        (window as any).__clearFillMeshWarnings();
    }
    
    // Only calculate bounds if we need to (for logging or translation checks)
    // Skip expensive logging in production or when not needed
    const shouldLogDetails = isFloorplannerDebugLoggingEnabled();
    const boundsBefore = shouldLogDetails ? calculateAndLogBounds(store, "BEFORE integrity checks") : null;
    
    // Check that all keys are unique
    const wallKeys = Array.from(store.wallsMap.keys());
    const symbolKeys = Array.from(store.symbolsMap.keys());
    const allKeys = wallKeys.concat(symbolKeys);
    const uniqueKeys = new Set(allKeys);
    if (uniqueKeys.size !== allKeys.length) {
        console.error("Duplicate keys found in walls and symbols", allKeys);
    }
    // Check all symbols attachments are valid
    store.symbolsMap.forEach((symbol) => {
        if (symbol.attachedTo) {
            const wall = store.wallsMap.get(symbol.attachedTo);
            if (!wall) {
                console.error("Symbol is attached to a wall that does not exist", symbol.id, symbol.attachedTo);
            } else {
                if (!wall.symbolAttachments) {
                    console.error("Symbol is attached to a wall that does not have symbol attachments", symbol.id, symbol.attachedTo);
                } else {
                    const attachment = wall.symbolAttachments.find(attachment => attachment.symbolId === symbol.id);
                    if (!attachment) {
                        console.error("Symbol is attached to a wall that does not have the symbol attachment", symbol.id, symbol.attachedTo);
                    }
                }
            }
        }
    });
    // Drop stale wall/edge connections that point at walls that no longer exist.
    // These leftover IDs poison joint/geometry generation; they are references,
    // not user objects, so removing them does not delete any walls.
    // Never setDirty() here — repaired topology stays in memory only until the
    // user edits and saves, so we never overwrite the cloud copy unprompted.
    let strippedDanglingConnectionCount = 0;
    const wallsWithStrippedConnections = new Set<string>();
    runInAction(() => {
        Array.from(store.wallsMap.values()).forEach((wall) => {
            if (wall.centerConnections && wall.centerConnections.length > 0) {
                const validCenterConnections = wall.centerConnections.filter((connection) => {
                    const connectedToWall = store.wallsMap.get(connection.id);
                    if (!connectedToWall) {
                        console.warn("[integrity] Removing dangling wall connection", wall.id, connection.id);
                        strippedDanglingConnectionCount += 1;
                        return false;
                    }
                    if (!connectedToWall.centerConnections?.find(
                        (connectedWallConnection) => connectedWallConnection.id === wall.id
                    )) {
                        console.error("Wall connection is not bidirectional", wall.id, connection.id);
                    }
                    return true;
                });
                if (validCenterConnections.length !== wall.centerConnections.length) {
                    wall.centerConnections = validCenterConnections;
                    wallsWithStrippedConnections.add(wall.id);
                }
            }

            if (wall.edgeConnections && wall.edgeConnections.length > 0) {
                // Only drop links to deleted walls. Do NOT strip geometrically
                // "impossible" joins on load — that would silently rewrite existing
                // customer plans. Gap validation applies on edit / new connections.
                const validEdgeConnections = wall.edgeConnections.filter((connection) => {
                    const targetWall = store.wallsMap.get(connection.targetWallId);
                    if (!targetWall) {
                        console.warn(
                            "[integrity] Removing edge connection to missing wall",
                            wall.id,
                            connection.targetWallId
                        );
                        strippedDanglingConnectionCount += 1;
                        return false;
                    }
                    return true;
                });
                if (validEdgeConnections.length !== wall.edgeConnections.length) {
                    wall.edgeConnections = validEdgeConnections;
                    wallsWithStrippedConnections.add(wall.id);
                }
            }
        });
    });
    // Check if all symbols have all required properties of their type by looking at the type property
    store.symbolsMap.forEach((symbol) => {
        if (isDoorType(symbol) || isSlidingDoorType(symbol)) {
            if (!symbol.doorWidth) {
                console.error("Door symbol does not have a door width", symbol.id);
            }
        } else if (isWindowType(symbol)) {
            if (!symbol.windowWidth) {
                console.error("Window symbol does not have a window width", symbol.id);
            }
            if (!symbol.windowLength) {
                console.error("Window symbol does not have a window length", symbol.id);
            }
        }
    });

    // Repair missing reciprocal edge connections (endpoint-to-endpoint only)
    const repairedEdgeConnections: string[] = [];
    store.wallsMap.forEach((wall) => {
        const edgeConnections = wall.edgeConnections || [];
        edgeConnections.forEach((connection) => {
            const targetWall = store.wallsMap.get(connection.targetWallId);
            if (!targetWall) {
                return;
            }

            // T-junctions intentionally one-way
            if (connection.targetEndpoint === "middle") {
                return;
            }

            const reciprocalExists = targetWall.edgeConnections?.some(
                (reciprocal) =>
                    reciprocal.targetWallId === wall.id &&
                    reciprocal.sourceEndpoint === connection.targetEndpoint &&
                    reciprocal.targetEndpoint === connection.sourceEndpoint
            );

            if (!reciprocalExists) {
                store.addEdgeConnection(
                    wall.id,
                    connection.targetWallId,
                    connection.sourceEndpoint,
                    connection.targetEndpoint,
                    connection
                );
                repairedEdgeConnections.push(
                    `${wall.id}->${connection.targetWallId}:${connection.sourceEndpoint}-${connection.targetEndpoint}`
                );
            }
        });
    });

    if (repairedEdgeConnections.length > 0) {
        console.warn(
            "[integrity] Repaired missing reciprocal edge connections",
            repairedEdgeConnections
        );
    }

    // Fix invalid walls/lines (zero length, NaN coordinates, etc.)
    // NEVER REMOVE ANYTHING - always fix/clamp to valid values
    // Objects with invalid coordinates will be moved to valid positions
    // IMPORTANT: Use snapshots to avoid iteration issues during modification
    let fixedInvalidCoordsCount = 0;

    const wallsSnapshot = Array.from(store.wallsMap.values());
    
    wallsSnapshot.forEach((wall) => {
        const hasInvalidCoords = 
            !Number.isFinite(wall.start.x) || !Number.isFinite(wall.start.y) ||
            !Number.isFinite(wall.end.x) || !Number.isFinite(wall.end.y);
        
        // Fix invalid coordinates by moving to valid positions
        if (hasInvalidCoords) {
            runInAction(() => {
                const updatedWall = store.wallsMap.get(wall.id);
                if (updatedWall) {
                    // Move to origin if coordinates are invalid
                    if (!Number.isFinite(updatedWall.start.x) || !Number.isFinite(updatedWall.start.y)) {
                        updatedWall.start = new THREE.Vector2(0, 0);
                    }
                    if (!Number.isFinite(updatedWall.end.x) || !Number.isFinite(updatedWall.end.y)) {
                        // If end is invalid but start is valid, extend from start
                        if (Number.isFinite(updatedWall.start.x) && Number.isFinite(updatedWall.start.y)) {
                            updatedWall.end = updatedWall.start.clone().add(new THREE.Vector2(MIN_WALL_LENGTH, 0));
                        } else {
                            updatedWall.end = new THREE.Vector2(MIN_WALL_LENGTH, 0);
                        }
                    }
                    fixedInvalidCoordsCount++;
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(
                            `[integrity] Fixed invalid coordinates for wall ${wall.id}, moved to valid position`
                        );
                    }
                }
            });
        }
    });

    const singleLinesSnapshot = Array.from(store.singleLinesMap.values());
    
    singleLinesSnapshot.forEach((line) => {
        const hasInvalidCoords = 
            !Number.isFinite(line.start.x) || !Number.isFinite(line.start.y) ||
            !Number.isFinite(line.end.x) || !Number.isFinite(line.end.y);
        
        // Fix invalid coordinates
        if (hasInvalidCoords) {
            runInAction(() => {
                const updatedLine = store.singleLinesMap.get(line.id);
                if (updatedLine) {
                    if (!Number.isFinite(updatedLine.start.x) || !Number.isFinite(updatedLine.start.y)) {
                        updatedLine.start = new THREE.Vector2(0, 0);
                    }
                    if (!Number.isFinite(updatedLine.end.x) || !Number.isFinite(updatedLine.end.y)) {
                        if (Number.isFinite(updatedLine.start.x) && Number.isFinite(updatedLine.start.y)) {
                            updatedLine.end = updatedLine.start.clone().add(new THREE.Vector2(MIN_WALL_LENGTH, 0));
                        } else {
                            updatedLine.end = new THREE.Vector2(MIN_WALL_LENGTH, 0);
                        }
                    }
                    fixedInvalidCoordsCount++;
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(
                            `[integrity] Fixed invalid coordinates for single line ${line.id}, moved to valid position`
                        );
                    }
                }
            });
        }
    });

    const rulerLinesSnapshot = Array.from(store.rulerLinesMap.values());
    
    rulerLinesSnapshot.forEach((line) => {
        const hasInvalidCoords = 
            !Number.isFinite(line.start.x) || !Number.isFinite(line.start.y) ||
            !Number.isFinite(line.end.x) || !Number.isFinite(line.end.y);
        
        // Fix invalid coordinates
        if (hasInvalidCoords) {
            runInAction(() => {
                const updatedLine = store.rulerLinesMap.get(line.id);
                if (updatedLine) {
                    if (!Number.isFinite(updatedLine.start.x) || !Number.isFinite(updatedLine.start.y)) {
                        updatedLine.start = new THREE.Vector2(0, 0);
                    }
                    if (!Number.isFinite(updatedLine.end.x) || !Number.isFinite(updatedLine.end.y)) {
                        if (Number.isFinite(updatedLine.start.x) && Number.isFinite(updatedLine.start.y)) {
                            updatedLine.end = updatedLine.start.clone().add(new THREE.Vector2(MIN_WALL_LENGTH, 0));
                        } else {
                            updatedLine.end = new THREE.Vector2(MIN_WALL_LENGTH, 0);
                        }
                    }
                    fixedInvalidCoordsCount++;
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(
                            `[integrity] Fixed invalid coordinates for ruler line ${line.id}, moved to valid position`
                        );
                    }
                }
            });
        }
    });

    if (fixedInvalidCoordsCount > 0 && process.env.NODE_ENV === 'development') {
        console.log(
            `[integrity] Fixed ${fixedInvalidCoordsCount} object(s) with invalid coordinates (moved to valid positions)`
        );
    }

    // Check and constrain objects larger than 300m, and clamp wall dimensions
    // NEVER REMOVE ANYTHING - always fix/clamp to valid values
    let constrainedCount = 0;
    let fixedGeometryIssuesCount = 0;
    let fixedSymbolSizesCount = 0;
    
    // Create snapshot to avoid iteration issues
    const wallsSnapshotForGeometry = Array.from(store.wallsMap.values());
    
    // Track problematic walls for detailed logging
    const problematicWallIds = new Set(['tnan742b1', 'q258uk4ky', 'iiamiq4h1']);
    
    wallsSnapshotForGeometry.forEach((wall) => {
        const isProblematic = problematicWallIds.has(wall.id);
        if (isProblematic && process.env.NODE_ENV === 'development') {
            console.log(`[integrity] Processing problematic wall ${wall.id}:`, {
                start: { x: wall.start.x, y: wall.start.y },
                end: { x: wall.end.x, y: wall.end.y },
                width: wall.wallWidth || store.wallWidth,
                length: wall.start.distanceTo(wall.end),
                controlPoint: wall.controlPoint,
                lineForm: wall.lineForm,
                symbolAttachments: wall.symbolAttachments?.length || 0
            });
        }
        // Fix invalid coordinates first - move to origin if invalid
        let needsCoordinateFix = false;
        let fixedStart = wall.start.clone();
        let fixedEnd = wall.end.clone();
        
        if (!Number.isFinite(wall.start.x) || !Number.isFinite(wall.start.y)) {
            fixedStart = new THREE.Vector2(0, 0);
            needsCoordinateFix = true;
        }
        if (!Number.isFinite(wall.end.x) || !Number.isFinite(wall.end.y)) {
            fixedEnd = fixedStart.clone().add(new THREE.Vector2(MIN_WALL_LENGTH, 0));
            needsCoordinateFix = true;
        }
        
        // Fix degenerate walls (start == end) - extend end point
        const direction = fixedEnd.clone().sub(fixedStart);
        if (direction.length() < 1e-6) {
            fixedEnd = fixedStart.clone().add(new THREE.Vector2(MIN_WALL_LENGTH, 0));
            needsCoordinateFix = true;
        }
        
        // Fix invalid control point (for arcs) - remove it to make wall straight
        let needsControlPointFix = false;
        if (wall.controlPoint && 
            (!Number.isFinite(wall.controlPoint.x) || !Number.isFinite(wall.controlPoint.y))) {
            needsControlPointFix = true;
        }
        
        // Clamp wall width to valid range
        const currentWidth = wall.wallWidth || store.wallWidth || 0.1;
        let clampedWidth = Math.max(MIN_WALL_WIDTH, Math.min(MAX_WALL_WIDTH, currentWidth));
        let needsWidthClamp = currentWidth !== clampedWidth;
        
        // Calculate wall length after coordinate fixes
        let wallLength = fixedStart.distanceTo(fixedEnd);
        
        // Clamp wall length
        let clampedLength = Math.max(MIN_WALL_LENGTH, Math.min(MAX_WALL_LENGTH, wallLength));
        let needsLengthClamp = wallLength !== clampedLength;
        
        // Calculate minimum required length based on width (must be at least 3x width)
        const minRequiredLength = Math.max(MIN_WALL_LENGTH, clampedWidth * MIN_LENGTH_TO_WIDTH_RATIO);
        
        // If clamped length is still too short relative to width, extend it
        if (clampedLength < minRequiredLength) {
            clampedLength = minRequiredLength;
            needsLengthClamp = true;
        }
        
        // Normalize direction and calculate new end point
        const finalDirection = fixedEnd.clone().sub(fixedStart).normalize();
        const finalEnd = fixedStart.clone().add(finalDirection.multiplyScalar(clampedLength));
        
        // Apply all fixes
        if (needsCoordinateFix || needsControlPointFix || needsWidthClamp || needsLengthClamp) {
            runInAction(() => {
                const updatedWall = store.wallsMap.get(wall.id);
                if (updatedWall) {
                    // Fix coordinates
                    if (needsCoordinateFix) {
                        updatedWall.start = fixedStart;
                        updatedWall.end = finalEnd;
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                `[integrity] Fixed invalid coordinates for wall ${wall.id}, moved to valid position`
                            );
                        }
                        fixedGeometryIssuesCount++;
                    }
                    
                    // Fix control point
                    if (needsControlPointFix) {
                        delete updatedWall.controlPoint;
                        updatedWall.lineForm = "straight";
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                `[integrity] Removed invalid control point from wall ${wall.id}, converted to straight wall`
                            );
                        }
                        fixedGeometryIssuesCount++;
                    }
                    
                    // Clamp width
                    if (needsWidthClamp) {
                        updatedWall.wallWidth = clampedWidth;
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                `[integrity] Clamped wall ${wall.id} width from ${currentWidth.toFixed(4)}m to ${clampedWidth.toFixed(4)}m`
                            );
                        }
                        fixedGeometryIssuesCount++;
                    }
                    
                    // Clamp/extend length
                    if (needsLengthClamp) {
                        updatedWall.end = finalEnd;
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                `[integrity] Clamped wall ${wall.id} length from ${wallLength.toFixed(4)}m to ${clampedLength.toFixed(4)}m ` +
                                `(width: ${clampedWidth.toFixed(4)}m, ratio: ${(clampedLength / clampedWidth).toFixed(2)}x)`
                            );
                        }
                        fixedGeometryIssuesCount++;
                    }
                }
            });
        }
        
        // Fix attached symbols that might create invalid clipping polygons
        if (wall.symbolAttachments && wall.symbolAttachments.length > 0) {
            const finalWallLength = clampedLength;
            wall.symbolAttachments.forEach((attachment) => {
                const symbol = store.symbolsMap.get(attachment.symbolId);
                if (!symbol) return;

                // Calculate symbol size
                let symbolSize = 0;
                if (isSlidingDoorType(symbol)) {
                    symbolSize = symbol.doorWidth * 2 + ((symbol.doorFrameWidth || store.doorFrameWidth) * 2);
                } else if (isDoorType(symbol)) {
                    const door = symbol as DoorType;
                    symbolSize = door.doorWidth + ((door.doorFrameWidth || store.doorFrameWidth) * 2);
                } else if (isDoubleDoorType(symbol)) {
                    const doubleDoor = symbol as DoubleDoorType;
                    symbolSize = doubleDoor.doubleDoorWidth * 2 + ((doubleDoor.doorFrameWidth || store.doorFrameWidth) * 2);
                } else if (isWindowType(symbol)) {
                    const window = symbol as WindowType;
                    symbolSize = window.windowLength;
                } else if (isVoidType(symbol)) {
                    const voidSymbol = symbol as VoidType;
                    symbolSize = voidSymbol.voidLength;
                } else {
                    symbolSize = 0.8; // Default size
                }

                // Clamp symbol size to not exceed 80% of wall length (to prevent zero-area clipping polygons)
                const maxSymbolSize = finalWallLength * 0.8;
                let symbolSizeWasClamped = false;
                if (symbolSize > maxSymbolSize) {
                    symbolSize = maxSymbolSize;
                    symbolSizeWasClamped = true;
                }

                // Calculate valid position range for the stored attachment anchor.
                // Doors/double doors store the hinge/anchor distance, not the center
                // of the opening, so endpoint anchors are valid and must survive load.
                const symbolHalfWidth = symbolSize / 2;
                const storesEndpointAnchor = isDoorType(symbol) || isSlidingDoorType(symbol) || isDoubleDoorType(symbol);
                const minValidPosition = storesEndpointAnchor ? 0 : symbolHalfWidth + 0.01;
                const maxValidPosition = storesEndpointAnchor ? finalWallLength : finalWallLength - symbolHalfWidth - 0.01;
                let positionWasClamped = false;
                let clampedPosition = attachment.positionFromStart;
                const originalPositionFromStart = attachment.positionFromStart;

                // Clamp attachment position to valid range
                if (!Number.isFinite(attachment.positionFromStart)) {
                    clampedPosition = minValidPosition;
                    positionWasClamped = true;
                } else if (attachment.positionFromStart < minValidPosition) {
                    clampedPosition = minValidPosition;
                    positionWasClamped = true;
                } else if (attachment.positionFromStart > maxValidPosition) {
                    clampedPosition = maxValidPosition;
                    positionWasClamped = true;
                }

                // Apply fixes if needed
                if (symbolSizeWasClamped || positionWasClamped) {
                    runInAction(() => {
                        const updatedSymbol = store.symbolsMap.get(attachment.symbolId);
                        if (updatedSymbol) {
                            // Fix symbol size if it was too large
                            if (symbolSizeWasClamped) {
                                if (isSlidingDoorType(updatedSymbol)) {
                                    const frameWidth = (updatedSymbol.doorFrameWidth || store.doorFrameWidth) * 2;
                                    const oldWidth = updatedSymbol.doorWidth;
                                    updatedSymbol.doorWidth = Math.max(0.1, (maxSymbolSize - frameWidth) / 2);
                                    if (process.env.NODE_ENV === 'development') {
                                        console.warn(
                                            `[integrity] Clamped sliding door ${symbol.id} width from ${oldWidth.toFixed(4)}m to ${updatedSymbol.doorWidth.toFixed(4)}m ` +
                                            `(wall length: ${finalWallLength.toFixed(4)}m)`
                                        );
                                    }
                                } else if (isDoorType(updatedSymbol)) {
                                    const door = updatedSymbol as DoorType;
                                    const frameWidth = (door.doorFrameWidth || store.doorFrameWidth) * 2;
                                    const oldWidth = door.doorWidth;
                                    door.doorWidth = Math.max(0.1, maxSymbolSize - frameWidth);
                                    if (process.env.NODE_ENV === 'development') {
                                        console.warn(
                                            `[integrity] Clamped door ${symbol.id} width from ${oldWidth.toFixed(4)}m to ${door.doorWidth.toFixed(4)}m ` +
                                            `(wall length: ${finalWallLength.toFixed(4)}m)`
                                        );
                                    }
                                } else if (isWindowType(updatedSymbol)) {
                                    const window = updatedSymbol as WindowType;
                                    const oldLength = window.windowLength;
                                    window.windowLength = Math.max(0.1, maxSymbolSize);
                                    if (process.env.NODE_ENV === 'development') {
                                        console.warn(
                                            `[integrity] Clamped window ${symbol.id} length from ${oldLength.toFixed(4)}m to ${window.windowLength.toFixed(4)}m ` +
                                            `(wall length: ${finalWallLength.toFixed(4)}m)`
                                        );
                                    }
                                } else if (isVoidType(updatedSymbol)) {
                                    const voidSymbol = updatedSymbol as VoidType;
                                    const oldLength = voidSymbol.voidLength;
                                    voidSymbol.voidLength = Math.max(0.1, maxSymbolSize);
                                    if (process.env.NODE_ENV === 'development') {
                                        console.warn(
                                            `[integrity] Clamped void ${symbol.id} length from ${oldLength.toFixed(4)}m to ${voidSymbol.voidLength.toFixed(4)}m ` +
                                            `(wall length: ${finalWallLength.toFixed(4)}m)`
                                        );
                                    }
                                }
                                fixedSymbolSizesCount++;
                            }

                            // Fix attachment position if it was out of bounds
                            if (positionWasClamped) {
                                const updatedWall = store.wallsMap.get(wall.id);
                                if (updatedWall) {
                                    attachment.positionFromStart = clampedPosition;
                                    // Update symbol position to match
                                    const insertGeom = getSymbolInsertGeometry(updatedWall, updatedSymbol, clampedPosition, store.doorFrameWidth);
                                    const renderCenterOffset = getSymbolRenderCenterOffset(updatedWall, updatedSymbol, clampedPosition, store.doorFrameWidth);
                                    const newSymbolPosition = insertGeom.midpoint
                                        .clone()
                                        .sub(insertGeom.tangent.clone().multiplyScalar(renderCenterOffset));
                                    store.symbolsMap.set(attachment.symbolId, {
                                        ...updatedSymbol,
                                        position: newSymbolPosition,
                                        rotation: Math.atan2(insertGeom.tangent.y, insertGeom.tangent.x) || 0.0001,
                                    });
                                    if (process.env.NODE_ENV === 'development') {
                                        console.warn(
                                            `[integrity] Clamped symbol ${symbol.id} position from ${originalPositionFromStart.toFixed(4)}m to ${clampedPosition.toFixed(4)}m ` +
                                            `(wall: ${wall.id}, wall length: ${finalWallLength.toFixed(4)}m, valid range: ${minValidPosition.toFixed(4)}-${maxValidPosition.toFixed(4)}m)`
                                        );
                                    }
                                }
                            }

                            // Note: Clipping polygons are generated at runtime during rendering,
                            // so we don't need to regenerate them here. The runtime will use
                            // the fixed symbol dimensions to generate valid clipping polygons.
                        }
                    });
                }
            });
        }

        // Final validation: Ensure the wall shape has sufficient area to prevent zero-area polygons
        // This catches edge cases where valid dimensions still produce degenerate geometry
        const finalWallForAreaCheck = store.wallsMap.get(wall.id);
        if (finalWallForAreaCheck) {
            const finalLength = finalWallForAreaCheck.start.distanceTo(finalWallForAreaCheck.end);
            const finalWidth = finalWallForAreaCheck.wallWidth || store.wallWidth || 0.1;
            const expectedArea = finalLength * finalWidth;
            const MIN_VALID_AREA = 0.0001; // Same threshold as fillMeshWalls.ts

            // Calculate actual shape corners to validate computed geometry, not just dimensions
            const lineWeight = store.convertLineWeightToWorld(finalWallForAreaCheck.lineWeight || store.wallLineWeight);
            const shapePaths = calculateLineVertices(
                finalWallForAreaCheck.start,
                finalWallForAreaCheck.end,
                0,
                lineWeight,
                finalWidth,
                finalWallForAreaCheck.lineForm === "arc" ? "arc" : "straight",
                finalWallForAreaCheck.controlPoint
            );

            // Validate the actual computed shape corners have sufficient area
            let actualShapeArea = 0;
            if (shapePaths.length >= 4) {
                // Calculate area using shoelace formula for the 4 corners
                const topLeft = shapePaths[0];
                const topRight = shapePaths[1];
                const bottomRight = shapePaths[2];
                const bottomLeft = shapePaths[3];

                // Shoelace formula: 0.5 * |sum(x_i * y_{i+1} - x_{i+1} * y_i)|
                actualShapeArea = 0.5 * Math.abs(
                    topLeft.x * topRight.y + topRight.x * bottomRight.y + bottomRight.x * bottomLeft.y + bottomLeft.x * topLeft.y -
                    (topRight.x * topLeft.y + bottomRight.x * topRight.y + bottomLeft.x * bottomRight.y + topLeft.x * bottomLeft.y)
                );
            }

            // Check if persisted corner state is corrupt (all corners at same point)
            // This happens when walls have invalid persisted corner state from previous saves
            const persistedCorners = store.getCornerStateForApplication(finalWallForAreaCheck);
            let hasCorruptCornerState = false;
            if (persistedCorners) {
                const cornerPoints = [
                    persistedCorners.topLeft,
                    persistedCorners.topRight,
                    persistedCorners.bottomRight,
                    persistedCorners.bottomLeft
                ].filter((c): c is { x: number; y: number } => c != null && typeof c.x === 'number' && typeof c.y === 'number');

                if (cornerPoints.length === 4) {
                    // Check if all corners are at the same point (or very close)
                    const firstCorner = cornerPoints[0];
                    const allSame = cornerPoints.every(c =>
                        Math.abs(c.x - firstCorner.x) < 0.001 && Math.abs(c.y - firstCorner.y) < 0.001
                    );
                    if (allSame) {
                        hasCorruptCornerState = true;
                    }
                }
            }

            // Use actual shape area for validation, not just expected area from dimensions
            if (actualShapeArea < MIN_VALID_AREA || hasCorruptCornerState) {
                const reason = actualShapeArea < MIN_VALID_AREA
                    ? `zero-area shape (actual: ${actualShapeArea.toFixed(6)}m², expected: ${expectedArea.toFixed(6)}m²)`
                    : `corrupt corner state (all corners at same point)`;
                console.warn(`[integrity] Wall ${wall.id} has ${reason}, fixing`);

                runInAction(() => {
                    const wallToUpdate = store.wallsMap.get(wall.id);
                    if (wallToUpdate) {
                        // Reset persisted corner state to force recomputation
                        if (wallToUpdate.cornerState) {
                            delete wallToUpdate.cornerState;
                        }

                        // Calculate minimum length needed for valid area based on actual shape computation
                        // The shape area is approximately length * width for straight walls
                        const minLengthForArea = Math.max(MIN_WALL_LENGTH, MIN_VALID_AREA / finalWidth + 0.01);
                        const direction = wallToUpdate.end.clone().sub(wallToUpdate.start);
                        if (direction.length() < 1e-6) {
                            // If start and end are essentially the same, create a horizontal wall
                            wallToUpdate.end = wallToUpdate.start.clone().add(new THREE.Vector2(minLengthForArea, 0));
                        } else {
                            direction.normalize();
                            const newEnd = wallToUpdate.start.clone().add(direction.multiplyScalar(minLengthForArea));
                            wallToUpdate.end = newEnd;
                        }
                        fixedGeometryIssuesCount++;
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                `[integrity] Fixed wall ${wall.id}: extended from ${finalLength.toFixed(4)}m to ${minLengthForArea.toFixed(4)}m, ` +
                                `reset corrupt corner state (width: ${finalWidth.toFixed(4)}m, expected area: ${(minLengthForArea * finalWidth).toFixed(6)}m²)`
                            );
                        }
                    }
                });
            }
        }

        // Also run the existing constrainWallOrLineSize for consistency
        // This ensures walls are clamped to MAX_WALL_LENGTH (100m) even if they passed geometry checks
        const wasConstrained = constrainWallOrLineSize(wall, store);
        if (wasConstrained) {
            constrainedCount++;
            // Re-check the wall after constraining to ensure it's now valid
            const updatedWall = store.wallsMap.get(wall.id);
            if (updatedWall) {
                const finalLength = updatedWall.start.distanceTo(updatedWall.end);
                if (updatedWall.controlPoint) {
                    const arcLength = calculateArcLength(updatedWall.start, updatedWall.controlPoint, updatedWall.end);
                    if (arcLength > MAX_WALL_LENGTH) {
                        console.warn(
                            `[integrity] Wall ${wall.id} still exceeds limit after constraint (arc length: ${arcLength.toFixed(2)}m), converting to straight`
                        );
                        runInAction(() => {
                            const wallToFix = store.wallsMap.get(wall.id);
                            if (wallToFix) {
                                const direction = wallToFix.end.clone().sub(wallToFix.start).normalize();
                                wallToFix.end = wallToFix.start.clone().add(direction.multiplyScalar(MAX_WALL_LENGTH));
                                wallToFix.controlPoint = undefined;
                            }
                        });
                    }
                } else if (finalLength > MAX_WALL_LENGTH) {
                    console.warn(
                        `[integrity] Wall ${wall.id} still exceeds limit after constraint (length: ${finalLength.toFixed(2)}m), re-clamping`
                    );
                    runInAction(() => {
                        const wallToFix = store.wallsMap.get(wall.id);
                        if (wallToFix) {
                            const direction = wallToFix.end.clone().sub(wallToFix.start).normalize();
                            wallToFix.end = wallToFix.start.clone().add(direction.multiplyScalar(MAX_WALL_LENGTH));
                        }
                    });
                }
            }
        }
        
        // Log final state for problematic walls
        if (isProblematic && process.env.NODE_ENV === 'development') {
            const finalWall = store.wallsMap.get(wall.id);
            if (finalWall) {
                console.log(`[integrity] After fixes for wall ${wall.id}:`, {
                    start: { x: finalWall.start.x, y: finalWall.start.y },
                    end: { x: finalWall.end.x, y: finalWall.end.y },
                    width: finalWall.wallWidth || store.wallWidth,
                    length: finalWall.start.distanceTo(finalWall.end),
                    ratio: (finalWall.start.distanceTo(finalWall.end) / (finalWall.wallWidth || store.wallWidth)).toFixed(2) + 'x'
                });
            }
        }
    });
    
    // Note: Clipping polygons are generated at runtime during rendering.
    // We only fix the underlying data structure (wall/symbol dimensions) here.
    // The runtime rendering will automatically generate valid clipping polygons
    // based on the fixed dimensions.
    
    // Log summary of fixes
    if (process.env.NODE_ENV === 'development') {
        if (fixedGeometryIssuesCount > 0) {
            console.log(
                `[integrity] Fixed ${fixedGeometryIssuesCount} wall(s) with geometry issues (clamped width/length, fixed coordinates)`
            );
        }
        if (fixedSymbolSizesCount > 0) {
            console.log(
                `[integrity] Fixed ${fixedSymbolSizesCount} symbol(s) attached to walls (clamped sizes to prevent invalid clipping polygons)`
            );
        }
        if (constrainedCount > 0) {
            console.log(
                `[integrity] Constrained ${constrainedCount} wall(s)/line(s) to maximum size`
            );
        }
    }
    store.singleLinesMap.forEach((line) => {
        if (constrainWallOrLineSize(line, store)) {
            constrainedCount++;
        }
    });
    store.rulerLinesMap.forEach((line) => {
        if (constrainWallOrLineSize(line, store)) {
            constrainedCount++;
        }
    });

    // Check and constrain area symbols
    store.symbolsMap.forEach((symbol) => {
        if (isAreaSymbolType(symbol) && symbol.vertices) {
            // Calculate bounding box of area symbol
            let minX = Number.POSITIVE_INFINITY;
            let minY = Number.POSITIVE_INFINITY;
            let maxX = Number.NEGATIVE_INFINITY;
            let maxY = Number.NEGATIVE_INFINITY;
            
            symbol.vertices.forEach(v => {
                minX = Math.min(minX, v.x);
                minY = Math.min(minY, v.y);
                maxX = Math.max(maxX, v.x);
                maxY = Math.max(maxY, v.y);
            });

            const width = maxX - minX;
            const height = maxY - minY;
            const maxDimension = Math.max(width, height);

            if (maxDimension > MAX_OBJECT_SIZE) {
                console.warn(
                    `[integrity] Constraining area symbol ${symbol.id} from ${maxDimension.toFixed(2)}m to ${MAX_OBJECT_SIZE}m`
                );
                
                // Scale down the vertices
                const scale = MAX_OBJECT_SIZE / maxDimension;
                const centerX = (minX + maxX) / 2;
                const centerY = (minY + maxY) / 2;
                
                const newVertices = symbol.vertices.map(v => {
                    const offsetX = v.x - centerX;
                    const offsetY = v.y - centerY;
                    return new THREE.Vector2(
                        centerX + offsetX * scale,
                        centerY + offsetY * scale
                    );
                });

                runInAction(() => {
                    const updatedSymbol: AreaSymbolType = {
                        ...symbol,
                        vertices: newVertices,
                    };
                    store.symbolsMap.set(symbol.id, updatedSymbol);
                });
                constrainedCount++;
            }
        }
    });

    if (constrainedCount > 0) {
        console.warn(
            `[integrity] Constrained ${constrainedCount} object(s) to maximum size of ${MAX_OBJECT_SIZE}m`
        );
    }

    // Check and fix connections positioned beyond 300m
    const fixedConnections = checkAndFixConnections(store);
    if (fixedConnections > 0) {
        console.warn(
            `[integrity] Fixed ${fixedConnections} connection(s) that were positioned beyond ${MAX_OBJECT_SIZE}m`
        );
    }

    // Check and fix object positions outside design area (includes outlier detection)
    const translatedObjects = checkAndFixObjectPositions(store);
    if (translatedObjects > 0) {
        console.warn(
            `[integrity] Translated ${translatedObjects} object(s) to center of design area`
        );
        
        // After moving outliers, re-check wall lengths to ensure they're still within limits
        // Outliers might have been very long walls that need clamping
        let reClampedCount = 0;
        const wallsAfterTranslation = Array.from(store.wallsMap.values());
        wallsAfterTranslation.forEach((wall) => {
            const length = wall.controlPoint && 
                Number.isFinite(wall.controlPoint.x) && Number.isFinite(wall.controlPoint.y)
                ? calculateArcLength(wall.start, wall.controlPoint, wall.end)
                : wall.start.distanceTo(wall.end);
            
            if (length > MAX_WALL_LENGTH) {
                console.warn(
                    `[integrity] Wall ${wall.id} still exceeds MAX_WALL_LENGTH after translation (${length.toFixed(2)}m), clamping`
                );
                runInAction(() => {
                    const wallToClamp = store.wallsMap.get(wall.id);
                    if (wallToClamp) {
                        const direction = wallToClamp.end.clone().sub(wallToClamp.start).normalize();
                        wallToClamp.end = wallToClamp.start.clone().add(direction.multiplyScalar(MAX_WALL_LENGTH));
                        // Remove controlPoint if present to make it straight
                        if (wallToClamp.controlPoint) {
                            wallToClamp.controlPoint = undefined;
                        }
                        reClampedCount++;
                    }
                });
            }
        });
        
        if (reClampedCount > 0) {
            console.warn(
                `[integrity] Re-clamped ${reClampedCount} wall(s) after outlier translation`
            );
        }
    }

    // Log bounds AFTER integrity checks (only if we logged before)
    const boundsAfter = shouldLogDetails ? calculateAndLogBounds(store, "AFTER integrity checks") : null;
    
    // Compare before and after (only if we have both)
    if (shouldLogDetails && boundsBefore && boundsAfter) {
        const centerDeltaX = boundsAfter.centerX - boundsBefore.centerX;
        const centerDeltaY = boundsAfter.centerY - boundsBefore.centerY;
        const centerDeltaDistance = Math.sqrt(centerDeltaX * centerDeltaX + centerDeltaY * centerDeltaY);
        
        console.log(`[integrity] Bounds comparison:`, {
            centerMoved: {
                before: { x: boundsBefore.centerX.toFixed(2), y: boundsBefore.centerY.toFixed(2) },
                after: { x: boundsAfter.centerX.toFixed(2), y: boundsAfter.centerY.toFixed(2) },
                delta: { x: centerDeltaX.toFixed(2), y: centerDeltaY.toFixed(2) },
                distance: centerDeltaDistance.toFixed(2)
            },
            sizeChanged: {
                before: { width: boundsBefore.width.toFixed(2), height: boundsBefore.height.toFixed(2) },
                after: { width: boundsAfter.width.toFixed(2), height: boundsAfter.height.toFixed(2) }
            }
        });
    }
    
    // Trigger wall updates after integrity fixes, but be selective to avoid performance issues
    // Only trigger full redraw for major changes (coordinate fixes, translations, or symbol size fixes)
    // For smaller fixes, just mark affected walls for update
    const shouldTriggerMajorRedraw =
        fixedInvalidCoordsCount > 0 ||
        translatedObjects > 0 ||
        fixedSymbolSizesCount > 0 ||
        fixedGeometryIssuesCount > 0;

    // initialize() already rebuilds wall geometry and fits the camera after load.
    // A competing redraw/resetZoom loop here freezes the tab on large plans.
    if (!store.isInitializing && shouldTriggerMajorRedraw) {
        if (shouldLogDetails) {
            console.log(
                `[integrity] Triggering full wall redraw after integrity fixes ` +
                `(fixed invalid coords: ${fixedInvalidCoordsCount}, translated: ${translatedObjects}, ` +
                `fixed geometry: ${fixedGeometryIssuesCount}, fixed symbols: ${fixedSymbolSizesCount})`
            );
        }
        void resetCameraAfterIntegrityRedraw(
            store,
            shouldLogDetails,
            options.resetCameraAfterRedraw !== false,
        );
    } else if (!store.isInitializing && (fixedConnections > 0 || strippedDanglingConnectionCount > 0)) {
        if (shouldLogDetails) {
            console.log(
                `[integrity] Updating walls after connection fixes ` +
                `(distance: ${fixedConnections}, dangling removed: ${strippedDanglingConnectionCount})`
            );
        }
        if (strippedDanglingConnectionCount > 0) {
            wallsWithStrippedConnections.forEach((wallId) => {
                store.objectNeedsUpdate(wallId);
            });
        }
        if (fixedConnections > 0) {
            store.wallsMap.forEach((wall) => {
                if (wall.centerConnections || wall.edgeConnections) {
                    store.objectNeedsUpdate(wall.id);
                }
            });
        }
    } else if (constrainedCount > 0) {
        // For size constraints, only update the constrained walls
        if (shouldLogDetails) {
            console.log(`[integrity] Updating ${constrainedCount} constrained walls`);
        }
        // The constrainWallOrLineSize function already updates the walls, so no additional update needed
    }
}
